import React, { useEffect } from "react";

import { navigate } from "@reach/router";
import DocumentService from "../../services/document_service";
import { useGovernor } from "@techempower/react-governor";
import { fake_state_todo_remove } from "../../constants/fake_state_todo_remove";
import { default_loan } from "../../schema/loan";
import { metadata } from "../../constants/utils";
import { default_account_profile } from "../../schema/profile";
import { processUserProfile, useUser } from "../../hooks/user";
import PageErrorBoundary from "./page_error_boundary";

export const DocumentContext = React.createContext();

const saveDocumentPromises = {};

// Outcome of the most recent save in this tab. The governor action only returns
// a state updater, so a caller that must NOT carry on after a failed save (the
// Update banner, before it reloads the tab) reads this right after awaiting it.
let lastSaveOk = true;
export function lastSaveSucceeded() {
  return lastSaveOk;
}

const contract = {
  setDocument(document) {
    return state => ({
      ...state,
      document: { ...document },
      // Mark unsaved edits so the Back button / tab-close warning can protect
      // the LO's work. Cleared on save and on a fresh load.
      dirty: true
    });
  },

  setDocumentName(name) {
    return state => ({
      ...state,
      name: name
    });
  },

  setPageTitleOverride(title) {
    return state => ({
      ...state,
      pageTitleOverride: title || null
    });
  },

  async loadDocument(id) {
    const timestamp = +new Date();
    // Reloading a loan (clicking "Update" inside a file, or any cold load) must
    // reliably reopen THIS loan. A transient blip must NEVER eject the LO to the
    // pipeline and lose their place (that showed a blank page, then the pipeline,
    // instead of the loan). We retry; on genuine repeated failure we stay on the
    // loan and flag loadFailed so the page shows a Retry — a Retry or reload
    // reopens the file right here. The LO is never bounced away.
    const MAX_ATTEMPTS = 6;
    let response = null;
    let lastStatus = 0;
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      try {
        const r = await DocumentService.getDocument(id);
        // Only a response that actually carries the document is success. A
        // resolved-but-empty/invalid body (a deploy hand-off can return one) is
        // treated as a transient failure and retried, not accepted as the loan.
        if (r && r.document) {
          response = r;
          break;
        }
      } catch (e) {
        // handleResponse throws the Response on a non-OK status; capture it so we
        // can tell an EXPIRED SESSION (401) from a transient network blip.
        lastStatus = (e && e.status) || 0;
      }
      if (attempt < MAX_ATTEMPTS - 1) {
        await new Promise(resolve => setTimeout(resolve, 500 * (attempt + 1)));
      }
    }
    if (!response) {
      // Never navigate("/"). An expired session (401) can't be fixed by a Retry,
      // so flag it "auth" — the page offers "Log in again", and because the login
      // screen renders at this same URL, logging back in reopens THIS loan. A
      // genuine transient failure stays "network" — the page offers Retry.
      const failure = lastStatus === 401 ? "auth" : "network";
      return state => ({ ...state, loadFailed: failure });
    }
    try {
      const documentAccountProfile = processUserProfile(
        response.documentUserProfile
      );
      const document = response.document;
      if (saveDocumentPromises[id]) {
        // The document has been saved at some point. Try to pull this saved document, possibly using it instead.
        try {
          const {
            documentType,
            document: documentState,
            name,
            metadata,
            timestamp: saveTimestamp
          } = await saveDocumentPromises[id];
          if (saveTimestamp > timestamp) {
            // The document was saved since the load was requested. Try to pull this saved document instead.
            document.type = documentType;
            document.name = name;
            document.content = JSON.stringify(documentState);
            document.metadata = JSON.stringify(metadata);
          }
        } catch (ignored) {
          // If the save failed, just use the requested version instead.
        }
      }

      const documentState = JSON.parse(document.content);
      // Take care defaults.
      Object.entries(fake_state_todo_remove).forEach(([key, value]) => {
        if (value && typeof value === "object" && !Array.isArray(value)) {
          documentState[key] = {
            ...value,
            ...documentState[key]
          };
        }
      });
      // Loan defaults.
      documentState.loans.forEach((loan, index) => {
        documentState.loans[index] = {
          ...default_loan,
          ...documentState.loans[index]
        };
        Object.entries(default_loan).forEach(([key, value]) => {
          if (value && typeof value === "object" && !Array.isArray(value)) {
            documentState.loans[index][key] = {
              ...value,
              ...documentState.loans[index][key]
            };
          }
        });
      });
      return state => ({
        ...state,
        documentAccountProfile,
        document: documentState,
        metadata: JSON.parse(document.metadata),
        documentId: document.id,
        name: document.name,
        documentType: document.type,
        loadFailed: false,
        // A freshly loaded loan has no unsaved edits yet.
        dirty: false
      });
    } catch (e) {
      // Parsing failed — don't eject; let the LO retry in place.
      return state => ({ ...state, loadFailed: "network" });
    }
  },

  setLoadFailed(failed) {
    return state => ({
      ...state,
      loadFailed: failed
    });
  },

  async saveDocument(getState) {
    try {
      const {
        documentType,
        document,
        documentId,
        name,
      } = getState();
      const meta = metadata(
        documentType,
        document,
        getState().metadata
      );
      const promise = (async () => {
        const { id } = await DocumentService.saveDocument(
          document,
          meta,
          documentId,
          name
        );
        // Add some extra data in the promise so loadDocument can read the saved document instead.
        return {
          documentType,
          document,
          id,
          name,
          metadata: meta,
          timestamp: +new Date()
        }
      })();

      if (documentId) {
        saveDocumentPromises[documentId] = promise;
      }

      const { id } = await promise;

      lastSaveOk = true;
      return state => ({
        ...state,
        metadata: meta,
        documentId: id,
        lastSaveDate: Date.now(),
        dirty: false,
        saveFailed: false
      });
    } catch (e) {
      // A failed save used to be silent, so the LO believed the loan was saved
      // and could then reload / update and lose the edits. Flag it instead: the
      // template shows a red NOT SAVED bar, and `dirty` stays set so the
      // tab-close warning keeps guarding the edits still on screen. A 401 is an
      // expired login session (it ends after 12 idle hours), which logging in
      // again fixes; anything else is treated as a connection problem.
      lastSaveOk = false;
      const failure = e && e.status === 401 ? "auth" : "network";
      return state => ({
        ...state,
        saveFailed: failure
      });
    }
  },

  setNavConnectorLinePositionOne(position) {
    return state => ({
      ...state,
      navConnectorLinePositionOne: position
    });
  },

  setNavConnectorLinePositionTwo(position) {
    return state => ({
      ...state,
      navConnectorLinePositionTwo: position
    });
  }
};

export function useDocument() {
  return React.useContext(DocumentContext);
}

export default function LoanTool({ documentId, children }) {
  const [state, actions] = useGovernor(
    {
      documentAccountProfile: { ...default_account_profile },
      document: { ...fake_state_todo_remove },
      documentId: { documentId },
      name: "",
      lastSaveDate: 0,
      navConnectorLinePositionOne: null,
      navConnectorLinePositionTwo: null,
      pageTitleOverride: null,
      loadFailed: false,
      dirty: false,
      saveFailed: false
    },
    contract
  );

  const [, userActions] = useUser();

  // Safety net: if the LO tries to close the tab, reload, or leave the site with
  // unsaved loan edits, the browser shows its native "Leave site?" confirmation.
  // Fires only when there are genuine unsaved changes (dirty), and never for
  // in-app navigation. Combined with the save-first Back buttons, this means no
  // loan data is silently lost.
  useEffect(() => {
    function warnIfUnsaved(e) {
      if (state.dirty) {
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
    }
    window.addEventListener("beforeunload", warnIfUnsaved);
    return () => window.removeEventListener("beforeunload", warnIfUnsaved);
  }, [state.dirty]);

  // const autoSaveTimeout = useRef(0);

  useEffect(() => {
    if (documentId) {
      actions.loadDocument(documentId);
    } else if (!state.document) {
      navigate("/");
    }
  }, []);

  useEffect(() => {
    document.title = state.pageTitleOverride || state.name;

    return () => {
      document.title = "LANIS TIER|ONE";
    };
  }, [state.name, state.pageTitleOverride]);

  // useEffect(() => {
  //   if (autoSaveTimeout.current) {
  //     clearTimeout(autoSaveTimeout.current);
  //   }
  //   // Auto save every 2 minutes.
  //   autoSaveTimeout.current = setTimeout(() => {
  //     actions.saveDocument();
  //   }, 120000);
  //
  //   return () => clearTimeout(autoSaveTimeout.current);
  // }, [state.lastSaveDate]);

  return (
    <DocumentContext.Provider
      value={[state.document, actions.setDocument, state, actions]}
    >
      {state.loadFailed === "auth" ? (
        <div
          className="no-print"
          style={{ padding: "60px 20px", display: "flex", justifyContent: "center" }}
        >
          <div
            style={{
              maxWidth: 460,
              textAlign: "center",
              border: "1px solid #d7e0e8",
              borderRadius: 10,
              padding: "28px 26px",
              background: "#fff"
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>
              Your session has expired
            </div>
            <div style={{ color: "#5a6b7a", marginBottom: 18 }}>
              Your work is safe. Please log in again &mdash; you&rsquo;ll come
              right back to this loan.
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button
                className="button"
                onClick={() => userActions.logout(() => {})}
              >
                Log in again
              </button>
            </div>
          </div>
        </div>
      ) : state.loadFailed ? (
        <div
          className="no-print"
          style={{ padding: "60px 20px", display: "flex", justifyContent: "center" }}
        >
          <div
            style={{
              maxWidth: 460,
              textAlign: "center",
              border: "1px solid #d7e0e8",
              borderRadius: 10,
              padding: "28px 26px",
              background: "#fff"
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>
              We couldn&rsquo;t open this loan just now
            </div>
            <div style={{ color: "#5a6b7a", marginBottom: 18 }}>
              Your work is safe. This can happen for a moment right after an
              update &mdash; try again and the file reopens right here.
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button
                className="button"
                onClick={() => {
                  actions.setLoadFailed(false);
                  if (documentId) {
                    actions.loadDocument(documentId);
                  }
                }}
              >
                Retry
              </button>
              <button
                className="button inverse-button"
                onClick={() => navigate("/")}
              >
                Back to pipeline
              </button>
            </div>
          </div>
        </div>
      ) : (
        state.document && state.documentType && (
          <PageErrorBoundary pageKey={typeof window !== "undefined" ? window.location.pathname : ""}>
            {children}
          </PageErrorBoundary>
        )
      )}
    </DocumentContext.Provider>
  );
}
